import type { ComponentType } from 'react';
import { AtSign, Video } from 'lucide-react';
import { SocialPlatform, socialPlatformLabel } from '@mg/shared';
import { LinkedInIcon, FacebookIcon, InstagramIcon, XIcon } from './brand-icons';

/** Any icon that accepts a `className` (lucide icons + our brand SVGs). */
type IconComponent = ComponentType<{ className?: string }>;

export interface PlatformMeta {
  label: string;
  Icon: IconComponent;
  /**
   * Tailwind classes for the rounded icon tile: the background in each theme, then the icon colour.
   * The tints are the spec's (opaque, with a dark-theme twin); Threads and X sit on the neutral
   * chip with the ink glyph, which turns light in the dark theme. The text class comes last.
   */
  tile: string;
  /**
   * The platform's brand colour as a TEXT class, on its own.
   *
   * Stated rather than picked out of `tile` by splitting the string: a chip that wants the colour
   * without the tint (the Analytics top-posts pill) would otherwise have to parse a class list and
   * would break silently the day one of these gains a hover or dark variant.
   */
  ink: string;
  /**
   * The colour the platform's own feed gives links and hashtags, as a TEXT class. Not the brand
   * colour: on X and Threads that is black, and Instagram's is pink, but their links are blue.
   * X's and Threads' own blues are about 3:1 on white, under the 4.5:1 WCAG AA asks of text, so
   * those two are the same hue darkened just enough to pass.
   */
  link: string;
}

/** Per-platform display metadata (label from shared; icon + brand accent here). */
export const PLATFORM_META: Record<SocialPlatform, PlatformMeta> = {
  [SocialPlatform.LINKEDIN]: {
    label: socialPlatformLabel[SocialPlatform.LINKEDIN],
    Icon: LinkedInIcon,
    tile: 'bg-platform-linkedin-tile dark:bg-platform-linkedin-tile-dark text-platform-linkedin',
    ink: 'text-platform-linkedin',
    link: 'text-[#0A66C2]',
  },
  [SocialPlatform.FACEBOOK]: {
    label: socialPlatformLabel[SocialPlatform.FACEBOOK],
    Icon: FacebookIcon,
    tile: 'bg-platform-facebook-tile dark:bg-platform-facebook-tile-dark text-platform-facebook',
    ink: 'text-platform-facebook',
    link: 'text-[#385898]',
  },
  [SocialPlatform.INSTAGRAM]: {
    label: socialPlatformLabel[SocialPlatform.INSTAGRAM],
    Icon: InstagramIcon,
    tile: 'bg-platform-instagram-tile dark:bg-platform-instagram-tile-dark text-platform-instagram',
    ink: 'text-platform-instagram',
    link: 'text-[#00376B]',
  },
  [SocialPlatform.THREADS]: {
    label: socialPlatformLabel[SocialPlatform.THREADS],
    Icon: AtSign,
    tile: 'bg-chip text-ink',
    ink: 'text-ink',
    link: 'text-[#007ACA]',
  },
  [SocialPlatform.X]: {
    label: socialPlatformLabel[SocialPlatform.X],
    Icon: XIcon,
    tile: 'bg-chip text-ink',
    ink: 'text-ink',
    link: 'text-[#177BBF]',
  },
  [SocialPlatform.YOUTUBE_SHORTS]: {
    label: socialPlatformLabel[SocialPlatform.YOUTUBE_SHORTS],
    Icon: Video,
    tile: 'bg-[#FF0000]/10 text-[#FF0000]',
    ink: 'text-[#FF0000]',
    link: 'text-[#065FD4]',
  },
};

/**
 * A platform by the name its brand writes, for a sentence or a picker row: "LinkedIn" and "YouTube
 * Shorts" (the enum title-cased reads "Linkedin" and "Youtube Shorts"), and "X" rather than the
 * label's "X / Twitter". Display only - never a stored value, an API value or an export key.
 */
export function platformName(platform: SocialPlatform): string {
  return platform === SocialPlatform.X ? 'X' : socialPlatformLabel[platform];
}
